import { WARNING_ORANGE } from "@finway-group/shared/lib/consts"
import { CostCenter, CostCenter2, Employee, Expense, ExpenseRequestTypeEnum, RightEnum, VendorTypeEnum } from "@finway-group/shared/lib/models"
import { Card, Tabs } from "antd"
import React from "react"
import { AlertTriangle } from "react-feather"
import { useTranslation } from "react-i18next"
import { useExpenseMatchedTransaction } from "../../../../shared/hooks/transaction.hooks"
import { useUserCreditorById } from "../../../../shared/hooks/vendor.hooks"
import { useExpenseCreditor } from "../../../../shared/queries/vendors/vendor.queries"
import { AuthzService } from "../../../../shared/services"
import { checkIsVendorPaymentDataMismatch, isFolderExpense, isInvoicePaymentDataDifferent } from "../../../../shared/utils/expense.utils"
import { FolderTabContent } from "./expenseSourceSection/folder.tab"
import { PurchaseTabContent } from "./expenseSourceSection/purchase.tab"
import { TransactionTabContent } from "./expenseSourceSection/transaction.tab"
import { VendorTabContent } from "./expenseSourceSection/vendor.tab"

interface ExpenseSourceSectionProps {
    expense: Expense
    requestedBy: Employee
    costCenter?: CostCenter
    costCenter2?: CostCenter2
    requestType: ExpenseRequestTypeEnum | undefined
    setIsExpenseUpdating?: React.Dispatch<React.SetStateAction<boolean>>
    isTransactionTableLoading?: boolean
    setSelectedExpense?: (expense: Expense | undefined) => void
    isExpenseUpdating?: boolean
    isOcrUploading?: boolean
    isReadOnly?: boolean
}

const { TabPane } = Tabs

const ExpenseSourceSection = ({
    expense,
    requestedBy,
    costCenter,
    costCenter2,
    requestType,
    setIsExpenseUpdating,
    isTransactionTableLoading = false,
    setSelectedExpense,
    isExpenseUpdating = false,
    isOcrUploading = false,
    isReadOnly = false,
}: ExpenseSourceSectionProps) => {
    const { t } = useTranslation()

    const userCreditor = useUserCreditorById(expense.getCreditor()?._id)
    const { data: vendorCreditor } = useExpenseCreditor(expense)

    const currentExpenseCreditor = userCreditor ?? vendorCreditor
    const isUserCreditor = expense?.vendorType === VendorTypeEnum.USER
    const sourceCreditor = isUserCreditor ? userCreditor : vendorCreditor

    const { transactionsToDisplay: transactions } = useExpenseMatchedTransaction(expense)

    return (
        <Card className="card-with-tabs" id="expense-source-section">
            <Tabs defaultActiveKey="purchaseData" className="mb-8">
                <TabPane
                    key={isFolderExpense(expense) ? "folderDetails" : "purchaseData"}
                    tab={isFolderExpense(expense) ? t("label:folder.trip_folder_details") : t("label:purchase_data")}
                >
                    {isFolderExpense(expense) ? (
                        <FolderTabContent expense={expense} requestedBy={requestedBy} costCenter={costCenter} costCenter2={costCenter2} isExpenseUpdating={isExpenseUpdating} />
                    ) : (
                        <PurchaseTabContent
                            expense={expense}
                            requestedBy={requestedBy}
                            costCenter={costCenter}
                            costCenter2={costCenter2}
                            vendorCreditor={vendorCreditor}
                            currentExpenseCreditor={currentExpenseCreditor}
                            requestType={requestType}
                            isReadOnly={isReadOnly}
                            isOcrUploading={isOcrUploading}
                            isExpenseUpdating={isExpenseUpdating}
                            setIsExpenseUpdating={setIsExpenseUpdating}
                            setSelectedExpense={setSelectedExpense}
                            key={`${expense.id}-${expense.__v}`}
                        />
                    )}
                </TabPane>
                {sourceCreditor && (isUserCreditor || AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR__ALL__READ)) && (
                    <TabPane
                        key="vendorData"
                        tab={
                            <span className="flex items-center">
                                {t("label:vendor_data")}
                                {checkIsVendorPaymentDataMismatch(expense) && isInvoicePaymentDataDifferent(expense) && (
                                    <AlertTriangle className="px-2 mt-2" fill={WARNING_ORANGE} size={24} stroke="#FFFFFF" />
                                )}
                            </span>
                        }
                    >
                        <VendorTabContent
                            isExpenseUpdating={isExpenseUpdating}
                            setSelectedExpense={setSelectedExpense}
                            expense={expense}
                            expenseCreditor={sourceCreditor}
                            setIsExpenseUpdating={setIsExpenseUpdating}
                        />
                    </TabPane>
                )}
                {transactions.length > 0 && (
                    <TabPane key="transactionData" tab={t("label:transaction_data")}>
                        <TransactionTabContent expense={expense} transactions={transactions} isTransactionTableLoading={isTransactionTableLoading} />
                    </TabPane>
                )}
            </Tabs>
        </Card>
    )
}

export default ExpenseSourceSection
